import React from 'react'
import { gql, useQuery } from '@apollo/client'

import { handleMutationError } from './errors'
import getTours from '../mutations/getTours'

// Der Anteil eines Restaurants am behebbaren Potenzial eines Orts, für eine Haushaltsgröße.
export type RestaurantPotentialHouseholdSize = {
	persons: number
	monthlyCustomers: number
	monthlyRevenue: number
	monthlyGroupOrders: number
	monthlyPlatformRevenue: number
}

export type SettlementRestaurant = {
	id: string
	// Dasselbe Restaurant über alle Orte, in die es liefert.
	slug: string
	lat: number | null
	lon: number | null
	name: string
	addressLine: string | null
	addressZipCode: string | null
	addressCity: string | null
	// Im Vertrieb von Hand eingetragen
	phone: string | null
	email: string | null
	// -3 (lohnt nicht) bis +3 (sehr wichtig)
	relevanceScore: number | null
	// Kette oder Franchise: schwer an Entscheider ranzukommen.
	isChain: boolean
	driveDistanceKm: number | null
	driveMinutes: number | null
	minOrderValue: number | null
	deliveryFee: number | null
	rating: number | null
	ratingCount: number | null
	// Küchen laut Lieferando; null, solange nie erhoben.
	cuisines: string[] | null
	// Hauptkategorie aus den Küchen; null für alles andere.
	cuisineCategory:
		| 'indian'
		| 'asian'
		| 'greek'
		| 'steak'
		| 'standard'
		| 'oriental'
		| 'sweet'
		| null
	isInDeliveryArea: boolean
	singleOrderMarginShare: number | null
	potentialBySize: RestaurantPotentialHouseholdSize[]
}

// Das volle Potenzial: alle, die bestellen würden und die Mitbestellt erreichen kann.
export type SettlementPotential = {
	inhabitants: number
	// Ab so vielen erreichbaren Restaurants ist eine Haushaltsgröße voll versorgt.
	fullSupplyRestaurants: number
	pricedOutShare: number
	fixableShare: number
	monthlyCustomers: number
	monthlyHouseholds: number
	monthlyLostRevenue: number
	orderValuePerPerson: number
	serviceFeeShare: number
	restaurantFeePerGroupOrder: number
	monthlyGroupOrders: number
	monthlyPlatformRevenue: number
	bySize: SettlementPotentialHouseholdSize[]
}

export type Settlement = {
	id: string
	state: string
	name: string
	placeType: string | null
	zipCode: string | null
	lat: number
	lon: number
	inhabitants5min: number | null
	inPortfolio: boolean
	deliveryCheckedOn: string | null
	// null, solange der Ort nie erhoben wurde; leer, wenn niemand liefert.
	restaurants: SettlementRestaurant[] | null
	ageAvg: number | null
	ageGroups: SettlementAgeGroup[]
	orderRate: number | null
	potential: SettlementPotential | null
}

// Dieselbe Rechenkette je Haushaltsgröße.
export type SettlementPotentialHouseholdSize = {
	persons: number
	inhabitants: number
	budget: number
	groupBudget: number
	reachable: number
	groupReachable: number
	pricedOutShare: number
	fixableShare: number
	monthlyCustomers: number
	monthlyHouseholds: number
	monthlyLostRevenue: number
	monthlyServiceFeeRevenue: number
	monthlyGroupOrders: number
	monthlyRestaurantFeeRevenue: number
	monthlyPlatformRevenue: number
}

export type SettlementAgeGroup = {
	label: string
	inhabitants: number
}

export const SETTLEMENTS_QUERY = gql`
	query Settlements($state: String!, $fullSupplyRestaurants: Int!) {
		settlements(state: $state) {
			id
			state
			name
			placeType
			zipCode
			lat
			lon
			inhabitants5min
			inPortfolio
			deliveryCheckedOn
			restaurants {
				id
				slug
				lat
				lon
				name
				addressLine
				addressZipCode
				addressCity
				phone
				email
				relevanceScore
				isChain
				driveDistanceKm
				driveMinutes
				minOrderValue
				deliveryFee
				rating
				ratingCount
				cuisines
				cuisineCategory
				isInDeliveryArea
				singleOrderMarginShare
				potentialBySize(fullSupplyRestaurants: $fullSupplyRestaurants) {
					persons
					monthlyCustomers
					monthlyRevenue
					monthlyGroupOrders
					monthlyPlatformRevenue
				}
			}
			ageAvg
			ageGroups {
				label
				inhabitants
			}
			orderRate
			potential(fullSupplyRestaurants: $fullSupplyRestaurants) {
				inhabitants
				fullSupplyRestaurants
				pricedOutShare
				fixableShare
				monthlyCustomers
				monthlyHouseholds
				monthlyLostRevenue
				orderValuePerPerson
				serviceFeeShare
				restaurantFeePerGroupOrder
				monthlyGroupOrders
				monthlyPlatformRevenue
				bySize {
					persons
					inhabitants
					budget
					groupBudget
					reachable
					groupReachable
					pricedOutShare
					fixableShare
					monthlyCustomers
					monthlyHouseholds
					monthlyLostRevenue
					monthlyServiceFeeRevenue
					monthlyGroupOrders
					monthlyRestaurantFeeRevenue
					monthlyPlatformRevenue
				}
			}
		}
	}
`

// Eine Spalte der Tourrechnung: heute 1 Haushalt, mit Mitbestellt 2 oder 3.
export type RestaurantTourColumn = {
	households: number
	isSingleOrder: boolean
	orderValue: number
	deliveryFee: number
	foodCost: number
	commission: number
	payment: number
	driveCost: number
	minutes: number
	profit: number
	householdCost: number
	personCount: number
	householdShare: number
	householdShareIsUpperBound: boolean
	personShare: number
}

export type RestaurantTour = {
	success: boolean
	error: string | null
	restaurantName: string | null
	city: string | null
	settlementName: string | null
	settlementInhabitants: number | null
	restaurantLat: number | null
	restaurantLon: number | null
	settlementLat: number | null
	settlementLon: number | null
	orderRate: number | null
	orderValuePerPerson: number | null
	deliveryAreaMinutes: number | null
	distanceKm: number | null
	durationMinutes: number | null
	mapImage: string | null
	// Die Strecke als [lon, lat]-Paare; null ohne gespeicherte Linie.
	routeCoordinates: [number, number][] | null
	// Das Restaurant liefert noch nicht dorthin, der MBW ist hochgerechnet.
	assumedMinOrderValue: boolean | null
	tours: RestaurantTourColumn[]
	householdSizeShares: { persons: number; share: number }[]
}

// Wofür die Tourrechnung gilt: eine Kombination aus Restaurant und Gemeinde, auch ohne Lieferbeziehung.
export type TourTarget = { slug: string; settlementId: string }

export const RESTAURANT_TOUR_QUERY = gql`
	query RestaurantTour($slug: String!, $settlementId: ID!) {
		restaurantTour(slug: $slug, settlementId: $settlementId) {
			success
			error
			restaurantName
			city
			settlementName
			settlementInhabitants
			restaurantLat
			restaurantLon
			settlementLat
			settlementLon
			orderRate
			orderValuePerPerson
			deliveryAreaMinutes
			distanceKm
			durationMinutes
			mapImage
			routeCoordinates
			assumedMinOrderValue
			tours {
				households
				isSingleOrder
				orderValue
				deliveryFee
				foodCost
				commission
				payment
				driveCost
				minutes
				profit
				householdCost
				personCount
				householdShare
				householdShareIsUpperBound
				personShare
			}
			householdSizeShares {
				persons
				share
			}
		}
	}
`

// Die Tourrechnung wie auf Seite 2 des Briefs.
// cacheOnly: liest nur, was die Tourkarte schon geladen hat, ohne eigenen Abruf bei Mapbox.
// Ohne target fragt der Hook nichts ab.
export const useRestaurantTour = (
	target: TourTarget | undefined,
	cacheOnly?: boolean
): {
	tour: RestaurantTour | undefined
	loading: boolean
} => {
	// Jedes Öffnen fragt neu, sonst zeigte die Karte bis zum Neuladen der Seite eine alte Rechnung.
	const { data } = useQuery(RESTAURANT_TOUR_QUERY, {
		variables: target,
		fetchPolicy: cacheOnly ? 'cache-only' : 'cache-and-network',
		skip: !target,
	})

	return React.useMemo(
		() => ({
			tour: data?.restaurantTour,
			loading: data?.restaurantTour === undefined,
		}),
		[data?.restaurantTour]
	)
}

export type RestaurantCommunication = {
	id: string
	slug: string
	channel: 'letter' | 'postcard' | 'email' | 'call' | 'note' | 'alarm'
	subject: string | null
	note: string | null
	reference: string | null
	// Nur bei Alarmen; verschickte Alarme liefert die API nicht mehr.
	dueAt: string | null
	createdAt: string
	settlementNames: string[]
}

// Ein Restaurant im Umkreis, das heute nicht in den Ort liefert. minOrderValue ist hochgerechnet: der
// MBW, bei dem die Fahrt so viel abwirft wie heute im Schnitt der belieferten Orte.
export type SettlementCandidate = {
	slug: string
	name: string
	addressCity: string | null
	rating: number | null
	ratingCount: number | null
	cuisines: string[] | null
	cuisineCategory: SettlementRestaurant['cuisineCategory']
	straightKm: number
	driveDistanceKm: number | null
	driveMinutes: number | null
	minOrderValue: number | null
	deliveryFee: number
	farthestSettlementName: string
	farthestDriveDistanceKm: number | null
	farthestDriveMinutes: number | null
	// Die Einzelfahrt hierher beim hochgerechneten MBW, wie in der Tourrechnung.
	singleTour: Pick<
		RestaurantTourColumn,
		'orderValue' | 'deliveryFee' | 'foodCost' | 'commission' | 'driveCost' | 'minutes' | 'profit'
	> | null
	// Fährt heute schon mindestens so weit, nur in andere Orte.
	reachesAlready: boolean
	servedSettlements: {
		settlementName: string
		minOrderValue: number | null
		marginShare: number | null
	}[]
	averageMarginShare: number | null
	singleOrderProfit: number | null
	singleOrderMarginShare: number | null
}

export const SETTLEMENT_CANDIDATES_QUERY = gql`
	query SettlementCandidates($settlementId: ID!) {
		settlementCandidates(settlementId: $settlementId) {
			slug
			name
			addressCity
			rating
			ratingCount
			cuisines
			cuisineCategory
			straightKm
			driveDistanceKm
			driveMinutes
			minOrderValue
			deliveryFee
			farthestSettlementName
			farthestDriveDistanceKm
			farthestDriveMinutes
			singleTour {
				orderValue
				deliveryFee
				foodCost
				commission
				driveCost
				minutes
				profit
			}
			reachesAlready
			servedSettlements {
				settlementName
				minOrderValue
				marginShare
			}
			averageMarginShare
			singleOrderProfit
			singleOrderMarginShare
		}
	}
`

// Potenzialanalyse eines Orts; ohne settlementId fragt der Hook nichts ab.
export const useSettlementCandidates = (
	settlementId: string | undefined
): {
	candidates: SettlementCandidate[]
	loading: boolean
} => {
	const { data } = useQuery(SETTLEMENT_CANDIDATES_QUERY, {
		variables: { settlementId },
		skip: !settlementId,
	})

	return React.useMemo(
		() => ({
			candidates: data?.settlementCandidates ?? [],
			loading: data?.settlementCandidates === undefined,
		}),
		[data?.settlementCandidates]
	)
}

// Eine im Vertrieb gemerkte Kombination aus Restaurant und Gemeinde; id ist "slug:settlementId".
export type RestaurantSettlementIdea = {
	id: string
	slug: string
	restaurantName: string
	settlementId: string
	settlementName: string
	state: string
	createdAt: string
}

export const RESTAURANT_SETTLEMENT_IDEAS_QUERY = gql`
	query RestaurantSettlementIdeas {
		restaurantSettlementIdeas {
			id
			slug
			restaurantName
			settlementId
			settlementName
			state
			createdAt
		}
	}
`

export const useRestaurantSettlementIdeas = (): {
	ideas: RestaurantSettlementIdea[]
	loading: boolean
} => {
	const { data } = useQuery(RESTAURANT_SETTLEMENT_IDEAS_QUERY)

	return React.useMemo(
		() => ({
			ideas: data?.restaurantSettlementIdeas ?? [],
			loading: data?.restaurantSettlementIdeas === undefined,
		}),
		[data?.restaurantSettlementIdeas]
	)
}

export const LETTER_PREVIEW_URL_QUERY = gql`
	query LetterPreviewUrl($printJobId: String!) {
		letterPreviewUrl(printJobId: $printJobId)
	}
`

export const RESTAURANT_COMMUNICATIONS_QUERY = gql`
	query RestaurantCommunications($slug: String) {
		restaurantCommunications(slug: $slug) {
			id
			slug
			channel
			subject
			note
			reference
			dueAt
			createdAt
			settlementNames
		}
	}
`

// Briefe, Postkarten, Mails, Anrufe und Notizen zu einem Restaurant, ohne Slug zu allen; neueste zuerst.
export const useRestaurantCommunications = (
	slug?: string
): {
	communications: RestaurantCommunication[]
	loading: boolean
} => {
	const { data } = useQuery(RESTAURANT_COMMUNICATIONS_QUERY, { variables: { slug } })

	return React.useMemo(
		() => ({
			communications: data?.restaurantCommunications ?? [],
			loading: data?.restaurantCommunications === undefined,
		}),
		[data?.restaurantCommunications]
	)
}

export const RESTAURANT_ALARMS_QUERY = gql`
	query RestaurantAlarms {
		restaurantAlarms {
			id
			slug
			restaurantName
			note
			dueAt
			notifiedAt
		}
	}
`

// Alle Alarme aller Restaurants, offene wie verschickte; spätester Termin zuerst.
export const useRestaurantAlarms = (): {
	alarms: {
		id: string
		slug: string
		restaurantName: string | null
		note: string | null
		dueAt: string
		notifiedAt: string | null
	}[]
	loading: boolean
} => {
	const { data } = useQuery(RESTAURANT_ALARMS_QUERY)

	return React.useMemo(
		() => ({
			alarms: data?.restaurantAlarms ?? [],
			loading: data?.restaurantAlarms === undefined,
		}),
		[data?.restaurantAlarms]
	)
}


// Holt beim Anzeigen fehlende Strecken der Restaurants in die Gemeinden; die API speichert sie.
export const useTours = (slugs: string[], settlementIds: string[]): void => {
	React.useEffect(() => {
		const loadTours = async () => {
			const result = await getTours({ slugs, settlementIds })
			if (!result.success) handleMutationError('getTours', result.errors)
		}
		if (slugs.length && settlementIds.length) loadTours()
	}, [settlementIds, slugs])
}

// fullSupplyRestaurants: ab so vielen erreichbaren Restaurants gilt eine Haushaltsgröße als versorgt.
export const useSettlements = (
	state: string,
	fullSupplyRestaurants: number
): {
	settlements: Settlement[]
	loading: boolean
} => {
	const { data } = useQuery(SETTLEMENTS_QUERY, { variables: { state, fullSupplyRestaurants } })

	return React.useMemo(
		() => ({
			settlements: data?.settlements ?? [],
			loading: data?.settlements === undefined,
		}),
		[data?.settlements]
	)
}
